<template>
  <div
    v-if="!actionBarActive && isLoadingDocument"
    class="loading_wrapper"
    :class="{ 'classic-theme-light-only': !inheritClassicTheme }"
  >
    <LoadingSpinner
      :color="inheritClassicTheme ? 'var(--classic-text, black)' : 'black'"
    />
  </div>

  <DocumentSpacePicker
    v-else-if="!actionBarActive && isShareable !== false"
    :documentId="documentId!"
    :inherit-classic-theme="inheritClassicTheme"
    @close="emitClose"
    @submit="submit"
  />

  <BottomActionBar
    v-if="actionBarActive"
    :class="{
      'classic-theme-light-only': !inheritClassicTheme,
      'share-theme': inheritClassicTheme,
    }"
  >
    <template #top>
      <div class="action_bar_progress" ref="undoProgress"></div>
    </template>
    <template #action>
      <template v-if="selectedSpaces.length === 1">
        <label>{{ $t("documentSpacePicker.undoBar.resultLabel.single") }}</label
        >:
        <router-link
          class="link"
          :to="{ name: 'DocumentSpace', params: { id: selectedSpaces[0] } }"
        >
          {{ $t("documentSpacePicker.undoBar.goToSpace") }}
        </router-link>
      </template>
      <template v-else>
        <label>{{
          $t("documentSpacePicker.undoBar.resultLabel.multiple", {
            count: selectedSpaces.length,
          })
        }}</label>
      </template>
    </template>
    <template #cancel>
      <button class="button right dangerous" @click="undo">
        {{ $t("undo") }}
      </button>
    </template>
  </BottomActionBar>
</template>

<script setup lang="ts">
import DocumentSpacePicker from "@/components/DocumentSpacePicker.vue";
import BottomActionBar from "@/components/common/BottomActionBar.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { computed, ref, onBeforeUnmount, watch } from "vue";
import {
  useCreateSpaceItemsMutation,
  useGetDocumentByIdQuery,
} from "@/graphql/operations";
import { useDocumentStore } from "@/store/document";
import { useFlashMessage } from "@/hooks/useFlashMessage";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

const documentStore = useDocumentStore();
const { displayFlashMessage } = useFlashMessage();
const { t } = useI18n();
const store = useStore();
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);

const emit = defineEmits(["close", "update"]);
const emitClose = () => emit("close");

const props = defineProps({
  inheritClassicTheme: { type: Boolean, default: false },
  docId: {
    type: String,
    required: false,
  },
});

const undoProgress = ref();

const { executeMutation: createDocumentSpaceItem } =
  useCreateSpaceItemsMutation();
const documentId = computed(() => props.docId ?? documentStore.id);
const { data: documentResult, fetching: fetchingDocument } =
  useGetDocumentByIdQuery({
    variables: computed(() => ({
      id: documentId.value!,
    })),
    pause: computed(() => !documentId.value),
    requestPolicy: "cache-first",
  });
const isShareable = computed<boolean | null>(() => {
  if (!documentId.value) {
    return false;
  }

  if (!documentResult.value?.document) {
    return null;
  }

  return documentResult.value.document.doesSupportSharedspace;
});
const isLoadingDocument = computed(
  () =>
    !!documentId.value && fetchingDocument.value && isShareable.value === null,
);
const hasClosedForNotShareable = ref(false);

const actionBarActive = ref<boolean>(false);

const hasOutstandingSave = ref(false);
const selectedSpaces = ref<Array<string>>([]);

let timeoutId: number | null;

watch(
  [documentId, isShareable, fetchingDocument, canUseSharedSpaces],
  ([currentDocumentId, currentIsShareable, isFetching, canUseSpaces]) => {
    if (!canUseSpaces) {
      emitClose();
      return;
    }
    if (!currentDocumentId) {
      emitClose();
      return;
    }

    if (currentIsShareable === false && !hasClosedForNotShareable.value) {
      hasClosedForNotShareable.value = true;
      hasOutstandingSave.value = false;
      displayFlashMessage(t("navbar.shareDocument.notShareable"), "error");
      emitClose();
      return;
    }

    if (!isFetching && currentIsShareable === null) {
      emitClose();
    }
  },
  { immediate: true },
);

const submit = (spaceIds: Array<string>) => {
  selectedSpaces.value = spaceIds;
  actionBarActive.value = true;
  hasOutstandingSave.value = true;

  timeoutId = window.setTimeout(async () => {
    await save();
    emit("close");
  }, 5000);
};

const undo = () => {
  if (timeoutId) {
    window.clearTimeout(timeoutId);
  }

  hasOutstandingSave.value = false;
  emit("close");
};

const save = async () => {
  if (!hasOutstandingSave.value) {
    return;
  }

  const targets = selectedSpaces.value.map((id: string) => ({ spaceId: id }));

  await createDocumentSpaceItem({
    documentId: documentId.value!,
    targets: targets,
  });

  hasOutstandingSave.value = false;
  emit("close");
};

window.addEventListener("beforeunload", save, false);

onBeforeUnmount(async () => {
  if (timeoutId) {
    window.clearTimeout(timeoutId);
    timeoutId = null;
  }

  window.removeEventListener("beforeunload", save, false);
  await save();
});
</script>

<style lang="scss" scoped>
@media (prefers-color-scheme: dark) {
  .share-theme {
    background: var(--classic-surface-raised, #363636);
    color: var(--classic-text, #fff);
    box-shadow: 0px 0px 8px 0px var(--classic-shadow, #00000040);
  }

  .share-theme :deep(button.button.right) {
    color: var(--classic-on-danger, #fff);
    border-color: var(--classic-text, #fff);
  }
}

.loading_wrapper {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
  background-color: var(--classic-surface, #fff);
}

.link {
  color: var(--classic-text, #fff);
  margin-left: 0.3em;
}

.action_bar_progress {
  background-color: var(--classic-danger, #d34a43);
  height: 4px;
  width: 0px;
  animation: fillProgress 5s ease-in-out forwards;
}

@keyframes fillProgress {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}
</style>
